import { useState, useEffect } from 'react';
import { Link, useNavigate, NavLink, Routes, Route } from 'react-router-dom';
import { Button, Space, Typography, Divider, Drawer, Dropdown } from 'antd';
import {
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
    EnvironmentOutlined,
    MenuOutlined,
    LogoutOutlined,
    AppstoreOutlined,
    InstagramOutlined,
    FacebookOutlined,
    SendOutlined,
    MessageOutlined,
    CarryOutOutlined,
    BankOutlined,
    LoginOutlined,
    MoonOutlined,
    SunOutlined,
} from '@ant-design/icons';
import useAuthStore from '../store/authStore';
import { isVendor } from '../utils/auth';
import Home from '../pages/site/Home';
import ServicesList from '../pages/site/ServicesList';
import '../pages/site/site.css';
import Services from '../pages/Services/Services';
import MapPage from '../pages/site/MapPage';
import ReportPage from '../pages/site/ReportPage';
import AboutPage from '../pages/site/AboutPage';
import ProfilePage from '../pages/profile/ProfilePage';
import ChatDrawer from '../components/Chat/ChatDrawer';
import WeddingPlannerPage from '../components/WeddingPlanner/WeddingPlanner';
import logo from './../assets/img/logo.png'
import { useTheme } from '../contexts/ThemeContext';

const { Text } = Typography;

const NAV_LINKS = [
    { label: 'Bosh sahifa', path: '/' },
    { label: 'Xizmatlar', path: '/services' },
    { label: 'Xaritada', path: '/map' },
    { label: 'Hamkor', path: '/report' },
    { label: 'Biz haqimizda', path: '/about' },
];

function Logo({ variant = 'dark' }) {
    const isLight = variant === 'light';
    return (
        <div className="qk-logo">
            <img src={logo} alt='Quvonchli kun' width={35} height={35} className="qk-logo-img" />
            <div>
                <div className={isLight ? 'qk-logo-title qk-logo-title-light' : 'qk-logo-title'}>
                    Quvonchli
                </div>
                <div className={isLight ? 'qk-logo-sub qk-logo-sub-light' : 'qk-logo-sub'}>
                    kun
                </div>
            </div>
        </div>
    );
}

function Navbar() {
    const { user, logout } = useAuthStore();
    const { isDark, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const vendor = isVendor();
    const GOV_ROLES = [1, 5, 6, 7, 8, 9, 10, 11];
    const isGov = user && GOV_ROLES.includes(user.roles_id);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <nav className={`qk-navbar${scrolled ? ' scrolled' : ''}`}>
            <Link to="/" className="qk-link-reset">
                <Logo />
            </Link>

            <Space size={4} className="qk-nav-desktop qk-nav-links">
                {NAV_LINKS.map((item) => (
                    <NavLink key={item.path} to={item.path} end={item.path === '/'}>
                        {item.label}
                    </NavLink>
                ))}
            </Space>

            <Space size={8}>
                <Button
                    icon={isDark ? <SunOutlined /> : <MoonOutlined />}
                    shape="circle"
                    className="qk-theme-toggle"
                    onClick={toggleTheme}
                    aria-label={isDark ? 'Kun rejimiga o‘tish' : 'Tun rejimiga o‘tish'}
                />
                {user ? (
                    <>
                        <Button
                            icon={<CarryOutOutlined />}
                            shape="circle"
                            onClick={() => navigate('/planner')}
                            className="qk-icon-btn qk-icon-btn-planner"
                        />
                        <Button
                            icon={<MessageOutlined />}
                            shape="circle"
                            onClick={() => setChatOpen(true)}
                            className="qk-icon-btn qk-icon-btn-chat"
                        />
                        {vendor && (
                            <Link to="/provider">
                                <Button type="primary" icon={<AppstoreOutlined />} shape="circle" />
                            </Link>
                        )}
                        {isGov && (
                            <Link to="/admingov">
                                <Button icon={<BankOutlined />} shape="circle"
                                    className="qk-icon-btn qk-icon-btn-gov"
                                />
                            </Link>
                        )}
                        <Dropdown
                            menu={{
                                items: [
                                    { label: <NavLink to="/profile">Profil</NavLink>, key: '1', icon: <UserOutlined /> },
                                    { label: <span onClick={logout} className="qk-danger-text">Chiqish</span>, key: '2', icon: <LogoutOutlined className="qk-danger-text" /> },
                                ],
                            }}
                            placement="bottomRight"
                        >
                            <Button type="primary" icon={<UserOutlined />} shape="circle" />
                        </Dropdown>
                    </>
                ) : (
                    <>
                        <Link to="/auth/login" className="qk-nav-auth-text">
                            <Button icon={<LoginOutlined />} type='primary' className="qk-gradient-btn" />
                        </Link>
                    </>
                )}
                <Button
                    icon={<MenuOutlined />}
                    type="text"
                    className="qk-nav-hamburger"
                    onClick={() => setDrawerOpen(true)}
                />
            </Space>

            <Drawer title={<Logo />} open={drawerOpen} onClose={() => setDrawerOpen(false)} placement="right" width={280}>
                <div className="qk-drawer-content">
                    <Button
                        icon={isDark ? <SunOutlined /> : <MoonOutlined />}
                        onClick={toggleTheme}
                        className="qk-drawer-theme-btn"
                    >
                        {isDark ? 'Kun rejimi' : 'Tun rejimi'}
                    </Button>
                    {NAV_LINKS.map((item) => (
                        <NavLink
                            key={item.path} to={item.path}
                            onClick={() => setDrawerOpen(false)}
                            className="qk-drawer-link"
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    {!user && (
                        <div className="qk-drawer-auth">
                            <Divider className="qk-drawer-divider" />
                            <Link to="/auth/login" onClick={() => setDrawerOpen(false)}>
                                <Button block className="qk-auth-secondary-btn">Kirish</Button>
                            </Link>
                            <Link to="/auth/register" onClick={() => setDrawerOpen(false)}>
                                <Button type="primary" block className="qk-gradient-btn">
                                    Ro'yxatdan o'tish
                                </Button>
                            </Link>
                        </div>
                    )}
                </div>
            </Drawer>
            <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
        </nav>
    );
}

function Footer() {
    return (
        <footer className="qk-footer">
            <div className="qk-footer-inner">
                <div className="qk-footer-grid">
                    <div>
                        <Logo variant="light" />
                        <p className="qk-footer-desc">
                            To'y va tantanalaringiz uchun barcha turdagi xizmatlar.
                            Restoranlar, honandalar, kamera va boshqalar.
                        </p>
                        <Space size={8} className="qk-footer-socials">
                            <a href="https://instagram.com" target="_blank" rel="noreferrer"
                                className="qk-footer-social-link">
                                <InstagramOutlined />
                            </a>
                            <a href="https://facebook.com" target="_blank" rel="noreferrer"
                                className="qk-footer-social-link">
                                <FacebookOutlined />
                            </a>
                            <a href="https://t.me/quvonchlikun" target="_blank" rel="noreferrer"
                                className="qk-footer-social-link">
                                <SendOutlined />
                            </a>
                        </Space>
                    </div>

                    <div>
                        <h4 className="qk-footer-title">Sahifalar</h4>
                        <div className="qk-footer-links">
                            <Link to="/">Bosh sahifa</Link>
                            <Link to="/services">Xizmatlar</Link>
                            <Link to="/map">Xaritada</Link>
                            <Link to="/about">Biz haqimizda</Link>
                        </div>
                    </div>

                    <div>
                        <h4 className="qk-footer-title">Xizmat turlari</h4>
                        <div className="qk-footer-links">
                            <Link to="/services?type=1">Restoranlar</Link>
                            <Link to="/services?type=2">Honandalar</Link>
                            <Link to="/services?type=3">Fotograf</Link>
                            <Link to="/services?type=4">To'y korteji</Link>
                        </div>
                    </div>

                    <div>
                        <h4 className="qk-footer-title">Aloqa</h4>
                        <div className="qk-footer-contact-list">
                            <div className="qk-footer-contact-item">
                                <PhoneOutlined className="qk-primary-icon" /> +998 99 018 76 65
                            </div>
                            <div className="qk-footer-contact-item">
                                <MailOutlined className="qk-primary-icon" /> info@quvonchli-kun.uz
                            </div>
                            <div className="qk-footer-contact-item">
                                <EnvironmentOutlined className="qk-primary-icon" /> samarqand, A.Temur 224
                            </div>
                        </div>
                    </div>
                </div>

                <Divider className="qk-footer-divider" />
                <div className="qk-footer-bottom">
                    <Text className="qk-footer-copy">
                        © 2026 Quvonchli Kun. Barcha huquqlar himoyalangan.
                    </Text>
                    <Space size={16}>
                        <Link to="/privacy" className="qk-footer-small-link">Maxfiylik siyosati</Link>
                        <Link to="/terms" className="qk-footer-small-link">Foydalanish shartlari</Link>
                    </Space>
                </div>
            </div>
        </footer>
    );
}

export default function SiteLayout() {
    return (
        <div className="qk-site-layout">
            <Navbar />
            <Routes>
                <Route path="map" element={<main className="qk-main"><MapPage /></main>} />
                <Route path="planner" element={<main className="qk-main"><WeddingPlannerPage /></main>} />
                <Route path="*" element={
                    <main className="qk-main qk-main-contained">
                        <Routes>
                            <Route index element={<Home />} />
                            <Route path="services" element={<ServicesList />} />
                            <Route path="services/:id" element={<Services />} />
                            <Route path="report" element={<ReportPage />} />
                            <Route path="profile" element={<ProfilePage />} />
                            <Route path="about" element={<AboutPage />} />
                            <Route path="*" element={<Home />} />
                        </Routes>
                    </main>
                } />
            </Routes>
            <Footer />
        </div>
    );
}